import type { Definition } from 'typescript-json-schema';
import type { ExtensionLogger, GenerateAppSchemaParameters } from '../../..';
import type { PresentationVariantV4 } from '../types';
import type { AnnotationTerm, ConvertedMetadata, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import type {
    Chart,
    DataFieldAbstractTypes,
    DataFieldForAction,
    LineItem
} from '@sap-ux/vocabularies-types/vocabularies/UI';
import i18next from 'i18next';
import { log } from '../../../extensionLogger';
import { UIAnnotationTerms, UIAnnotationTypes } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type { EntityTypeAnnotationsBase_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import {
    v4,
    ActionType,
    FioriElementsVersion,
    ManifestSection,
    SectionType,
    Visualization,
    DefinitionName,
    SchemaKeyName,
    MANIFESTPATH,
    LogSeverity,
    SchemaTag,
    FacetBase,
    UIVOCABULARYDOT,
    UIVOCABULARYALPHADOT,
    Features,
    ArtifactType,
    PageTypeV4
} from '@sap/ux-specification-types';
import type { FacetConfig, FacetConfigs, Manifest } from '@sap/ux-specification-types';
import {
    DEFINITION_LINK_PREFIX,
    addCommonHeaderSchema,
    addEnumForSingleTabVariant,
    addFooterActions,
    addHeaderActions,
    addSectionTitle,
    addSectionDescription,
    addTargetTitleAndSectionDescription,
    createDefaultSection,
    determineDataType,
    determineDataTypeOfTarget,
    getActionNameAndId,
    getDataFieldDescription,
    getFacetAnnotationPath,
    getFacetDefinitionLink,
    getFacetKeyInSchema,
    getObjectPageFacets,
    getObjectPageHeaderFacets,
    handleAddressContact,
    handleForm,
    parseSchemaDefinition,
    prepareRef,
    replaceAlias,
    updatePropertyIndices,
    isFeatureSupported,
    addDescriptiveEnumDefinition,
    getFacetDefinitionKey,
    addChartFacetID,
    getPropertiesForStandardActionAndActionGroup,
    type ManifestPathParams
} from '../../common';
import {
    addCustomFieldDefinition,
    addManifestPathsToProperties,
    addOneOfForCreationFields,
    getInsertableForEntitySet,
    getPageManifestPath,
    type LineItemDataForSchema
} from '../utils/utils';
import {
    addHeaderFooterCustomActionDefinition,
    addCustomSectionDefinition,
    addCustomColumnDefinition,
    addFragmentEnumForAnchor,
    addLineItemsTypeToSchema,
    findPageV4,
    addCustomActionDefinition,
    addEnumForActionAnchor,
    addEnumForProperties,
    addEnumForEnableMassEdit
} from '../utils/utils';
import { getStableIdPartFromDataField } from '../../v4/utils/StableIdHelper';
import { resolveFragments } from './fpm-custom-page/pageExtensions';
import { isDraftEnabled, isEntityDeletable, isEntityUpdatable } from '../../v2/generate/utils';
import { type SapUiAppPageV4 } from '@sap/ux-specification-types/src/v4';
import * as v4controls from '../export/controls';
import {
    configureAdditionalPropertiesForCustomActionsAndMenus,
    buildAdditionalPropertiesSchemaForCustomActionsAndMenus
} from './actions';
import { ObjectPageCustomSubSectionFragment } from '../export/controls/ObjectPageCustomSection';
import { Decorator } from '../../common/decoration/Decorator';
import type { DecoratorContext } from '../../common/decoration/interfaces';
import { TableCreationModeOP } from '../export/controls/ObjectPageTable';
import { AppSchema } from '../sync-rules/AppSchema';

const ROOT_PROPERTIES_ORDER: Array<keyof v4.ObjectPageConfigV4> = ['header', 'layout', 'sections', 'footer'];
const CRITICAL_ACTION_TYPES = [
    'UI.CriticalityType/Positive',
    'UI.CriticalityType/VeryPositive',
    'UI.CriticalityType/Negative',
    'UI.CriticalityType/VeryNegative'
];
const CUSTOM_ACTION_DEFINITION_DESCRIPTION = 'Custom Action';
const CUSTOM_FIELD_DEFINITION_DESCRIPTION = 'Custom Field';

type ActionListType = {
    index: string;
    item: object;
    isInnerAction?: boolean;
};

type VisualizationProps = {
    key: string;
    type: Visualization;
    target: object;
    namespace: string;
    navigationPath: string;
};

interface AddTableDefinitionProps {
    tableType: string;
    facetKey: string;
    facet: FacetConfig;
    appSchema: Definition;
    lineItemId: string;
    lineItemFacet: FacetConfig;
    v4Page: v4.SapUiAppPageV4;
    isCollection?: boolean;
    generateParameters: GenerateAppSchemaParameters;
}

/**
 * V4 callback - adds a single header action to schema.
 *
 * @param oDataServiceAVT - Complete service information, as returned by annotation vocabularies tool.
 * @param action - Item of the Ui.Identification collection and its original index.
 * @param actionType - Action classification for grouping.
 * @param appSchema - Schema of the app.
 * @param entityType - Current entity type.
 * @param headerActions - List of header actions, to be enhanced.
 * @param index - The index of the action in the header actions list.
 */
function addSingleHeaderActionV4(
    oDataServiceAVT: ConvertedMetadata,
    action: ActionListType,
    actionType: ActionType,
    appSchema: Definition,
    entityType: EntityType,
    headerActions: Definition,
    index: number
): void {
    const { actionName, namespace } = getActionNameAndId(action.item, oDataServiceAVT);
    const stableId = getStableIdPartFromDataField(action.item);
    const actionEntityType: UIAnnotationTypes = action.item['$Type'] as UIAnnotationTypes;
    const isActionGroup = actionEntityType === UIAnnotationTypes.DataFieldForActionGroup;
    const actionDefinitionType = isActionGroup
        ? DefinitionName.ObjectPageHeaderActionGroup
        : action.isInnerAction
          ? DefinitionName.ObjectPageHeaderActionBase
          : DefinitionName.ObjectPageHeaderAction;

    //stableId is used as key in V4, as the DataFieldForAction:: prefix shall be present in manifest settings
    const convertedIdentification = action.item['fullyQualifiedName'].replace(
        `@${UIAnnotationTerms.Identification}`,
        `/@${UIAnnotationTerms.Identification}`
    );
    const actionDefinitionKey = `${actionDefinitionType}<${prepareRef(stableId)}>`;
    const headerAction = (appSchema.definitions[actionDefinitionKey] = {
        type: 'object',
        $ref: `${DEFINITION_LINK_PREFIX}${actionDefinitionType}`,
        description: getDataFieldDescription(action.item as DataFieldAbstractTypes, entityType)
    });
    headerAction[SchemaTag.isViewNode] = true;
    headerAction[SchemaTag.dataType] = determineDataType(action.item);
    headerAction[SchemaTag.propertyIndex] = index;

    if (isActionGroup) {
        const properties = getPropertiesForStandardActionAndActionGroup(
            appSchema,
            DefinitionName.ObjectPageHeaderActionGroup
        );
        headerAction['properties'] = {
            ...properties
        };
        const subActions: ActionListType[] = (action.item['Actions'] ?? []).map(
            (groupedAction, index) =>
                ({
                    item: groupedAction,
                    index,
                    isInnerAction: true
                }) as ActionListType
        );

        addGroupedHeaderActions(
            subActions,
            oDataServiceAVT,
            appSchema,
            entityType,
            headerAction,
            ActionType.Annotation
        );
    } else {
        //keys
        headerAction[SchemaTag.keys] = [];
        if (action.item[SchemaKeyName.semanticObject]) {
            headerAction[SchemaTag.keys].push({
                name: SchemaKeyName.semanticObject,
                value: action.item[SchemaKeyName.semanticObject]
            });
        }
        headerAction[SchemaTag.keys].push({ name: SchemaKeyName.action, value: actionName });
    }

    if (namespace) {
        appSchema.definitions[actionDefinitionKey][SchemaTag.target] = namespace;
    }
    headerAction[SchemaTag.actionType] = actionType;
    headerAction[SchemaTag.annotationPath] = `/${convertedIdentification}`;
    headerActions.properties[stableId] = {
        $ref: `${DEFINITION_LINK_PREFIX}${actionDefinitionKey}`
    };

    // add propertyIndexes
    headerActions.properties[stableId][SchemaTag.propertyIndex] = index;
}

/**
 * Adds a given standard action to the app schema of the object page.
 *
 * @param actionName - Name of the action.
 * @param appSchema - Schema of the object page.
 * @param headerActions - List of header actions, to be enhanced.
 * @param index - Index for propertyIndex.
 * @returns The new header action definition.
 */
function addStandardHeaderAction(
    actionName: string,
    appSchema: Definition,
    headerActions: Definition,
    index: number
): Definition {
    const referenceKey = `${actionName}Action`;
    const actionDefinitionKey = `ObjectPageHeaderAction<${referenceKey}>`;
    // standard action properties
    const properties = getPropertiesForStandardActionAndActionGroup(
        appSchema,
        DefinitionName.ObjectPageHeaderActionGroup
    );
    const headerAction = (appSchema.definitions[actionDefinitionKey] = {
        type: 'object',
        description: `${actionName}`,
        ...(Object.keys(properties).length > 0 && { properties }),
        additionalProperties: true
    });
    headerAction[SchemaTag.isViewNode] = true;
    headerAction[SchemaTag.propertyIndex] = index;

    //keys
    headerAction[SchemaTag.keys] = [];
    headerAction[SchemaTag.keys].push({ name: SchemaKeyName.action, value: actionName });

    headerAction[SchemaTag.actionType] = ActionType.Standard;
    headerActions.properties[referenceKey] = {
        $ref: `${DEFINITION_LINK_PREFIX}${actionDefinitionKey}`
    };
    // add propertyIndexes
    headerActions.properties[referenceKey][SchemaTag.propertyIndex] = index;
    return headerAction;
}

/**
 * V4 callback - adds a single footer action to schema.
 *
 * @param action - Item of the Ui.Identification collection and its original index.
 * @param actionType - Action classification for grouping.
 * @param entityType - Current entity type.
 * @param appSchema - Schema of the object page.
 * @param footerActions - List of footer actions, to be enhanced.
 * @param index - The index of the action in the footer actions list.
 */
function addSingleFooterAction(
    action: ActionListType,
    actionType: ActionType,
    entityType: any,
    appSchema: Definition,
    footerActions: Definition,
    index: number
) {
    const actionId = getStableIdPartFromDataField(action.item);
    const description = getDataFieldDescription(action.item as DataFieldAbstractTypes, entityType);
    const actionDefinitionKey = prepareRef(`${DefinitionName.ObjectPageFooterAction}<${actionId}>`);
    const convertedIdentification = action.item['fullyQualifiedName'].replace(
        `@${UIAnnotationTerms.Identification}`,
        `/@${UIAnnotationTerms.Identification}`
    );
    const footerAction = (appSchema.definitions[actionDefinitionKey] = {
        type: 'object',
        $ref: `${DEFINITION_LINK_PREFIX}${DefinitionName.FooterActionV4}`,
        description: description
    });
    footerAction[SchemaTag.isViewNode] = true;
    footerAction[SchemaTag.actionType] = actionType;
    footerAction[SchemaTag.propertyIndex] = index;
    footerAction[SchemaTag.annotationPath] = `/${convertedIdentification}`;

    footerActions.properties[actionId] = {
        $ref: `${DEFINITION_LINK_PREFIX}${actionDefinitionKey}`
    };
}

/**
 * Distinguishes critical actions and others and returns two separate groups.
 *
 * @param identification - UI.Identification annotation as present in AVT ConvertedMetadata.
 * @returns Two separate groups of items.
 */
function groupByCriticality(identification: any) {
    const criticalActions: ActionListType[] = [];
    const unCriticalActions: ActionListType[] = [];

    identification.forEach((item, index) => {
        if (
            (!item['Determining'] && item.$Type === UIAnnotationTypes.DataFieldForAction) ||
            item.$Type === UIAnnotationTypes.DataFieldForActionGroup ||
            item.$Type === UIAnnotationTypes.DataFieldForIntentBasedNavigation
        ) {
            if (CRITICAL_ACTION_TYPES.includes(item['Criticality'])) {
                criticalActions.push({ index, item });
            } else {
                unCriticalActions.push({ index, item });
            }
        }
    });
    return { criticalActions, unCriticalActions };
}

/**
 * Checks if one of the uncritical actions is marked by isCopyAction.
 * If so, adds this first to the schema and deletes it from the list.
 *
 * @param {ActionListType[]} unCriticalActions - list of uncritical actions
 * @param {ConvertedMetadata} oDataServiceAVT - complete service information, as returned by annotation vocabularies tool
 * @param {Definition} appSchema - schema of the object page
 * @param {EntityType} entityType - current entity type
 * @param {Definition} headerActions - list of all header actions
 * @param {number} nextIndex - index for propertyIndex
 */
function addCopyAction(
    unCriticalActions: ActionListType[],
    oDataServiceAVT: ConvertedMetadata,
    appSchema: Definition,
    entityType: EntityType,
    headerActions: Definition,
    nextIndex: number
) {
    const copyActionIndex = unCriticalActions.findIndex((action) => {
        return action.item['annotations']?.UI?.IsCopyAction !== undefined;
    });
    if (copyActionIndex > -1) {
        addSingleHeaderActionV4(
            oDataServiceAVT,
            unCriticalActions[copyActionIndex],
            ActionType.Copy,
            appSchema,
            entityType,
            headerActions,
            nextIndex
        );
        nextIndex++;
        unCriticalActions.splice(copyActionIndex, 1);
    }
}

/**
 * Adds a list of actions to the header actions in the application schema.
 *
 * @param {ActionListType[]} actions - The list of actions to be added.
 * @param {ConvertedMetadata} oDataServiceAVT - The converted metadata service containing OData service annotations.
 * @param {Definition} appSchema - The application schema definition where actions will be added.
 * @param {EntityType} entityType - The entity type for which the actions are applicable.
 * @param {Definition} headerActions - The definition where the header actions will be appended.
 * @param {ActionType} actionType - The type of actions to be processed and added.
 * @param {number} [startingIndex] - The starting index for assigning actions in the header actions definition.
 */
export function addGroupedHeaderActions(
    actions: ActionListType[],
    oDataServiceAVT: ConvertedMetadata,
    appSchema: Definition,
    entityType: EntityType,
    headerActions: Definition,
    actionType: ActionType,
    startingIndex: number = 0
) {
    let nextIndex = startingIndex;
    for (const action of actions) {
        addSingleHeaderActionV4(oDataServiceAVT, action, actionType, appSchema, entityType, headerActions, nextIndex);
        nextIndex++;
    }
}

/**
 * Adds the critical header actions to schema.
 *
 * @param {Definition} headerActions - list of all header actions
 */
function addRelatedAppsButton(headerActions: Definition) {
    const actionName = 'RelatedApps';
    const newAction = (headerActions.properties[actionName] = {
        $ref: `${DEFINITION_LINK_PREFIX}${actionName}`,
        description: 'Related Apps'
    });
    // add tags
    newAction[SchemaTag.propertyIndex] = Object.keys(headerActions.properties).length - 1;
    newAction[SchemaTag.artifactType] = ArtifactType.Manifest;
    newAction[SchemaTag.actionType] = ActionType.Standard;
    newAction[SchemaTag.isViewNode] = true;
    newAction[SchemaTag.keys] = [{ name: SchemaKeyName.action, value: actionName }];
}

/**
 * Fills the annotationPath of the given schema definition, by checking first the EntitySet annotation, then the EntityType annotation.
 *
 * @param {Definition} schemaDefinition - current definition in the schema
 * @param {EntitySet} entitySet - current entity set
 * @param {EntityType} entityType - current entity type
 * @param {string} annotationName - name of the annotation term to be checked
 */
function setAnnotationPathFromEntitySetOrEntityType(
    schemaDefinition: Definition,
    entitySet: EntitySet,
    entityType: EntityType,
    annotationName: string
) {
    if (entitySet?.annotations?.UI?.[annotationName] || !entityType?.annotations?.UI?.[annotationName]) {
        schemaDefinition[SchemaTag.annotationPath] =
            `/${entitySet.fullyQualifiedName}/${UIVOCABULARYALPHADOT}${annotationName}`;
    } else {
        schemaDefinition[SchemaTag.annotationPath] =
            `/${entityType.fullyQualifiedName}/${UIVOCABULARYALPHADOT}${annotationName}`;
    }
}

/**
 * Callback for adding a single action to headerActions of V4.
 *
 * @param entityType - current entity type
 * @param appSchema - schema of the object page
 * @param identification - UI.Identification annotation as present in AVT ConvertedMetadata
 * @param headerActions - list of header actions, to be enhanced
 * @param generateParameters - list of API input parameters
 * @param pageInManifest - current page in manifest
 */
function addHeaderActionCallBackV4(
    entityType: EntityType,
    appSchema: Definition,
    identification: EntityTypeAnnotationsBase_UI['Identification'] | undefined,
    headerActions: Definition,
    generateParameters: GenerateAppSchemaParameters,
    pageInManifest: v4.SapUiAppPageV4
): any {
    //Build groups of action types
    const { criticalActions = [], unCriticalActions = [] } = identification ? groupByCriticality(identification) : {};

    addGroupedHeaderActions(
        criticalActions,
        generateParameters.serviceAVT,
        appSchema,
        entityType,
        headerActions,
        ActionType.Criticality
    );

    if (generateParameters.entitySet) {
        const isDeletable = isEntityDeletable(generateParameters.entitySet);
        const isUpdatable = isEntityUpdatable(generateParameters.entitySet);
        // Add standard header actions

        // If ReadOnly, and all capabilities restrictions are set to true or When nothing exists i.e. no capabilities annotation and no draft root, show only Delete button
        // If DraftRoot + DraftNode and dynamic value for restriction - show Edit and Delete, if bool == false then hide the button

        if (isDraftEnabled(generateParameters.entitySet) && isUpdatable) {
            const editAction = addStandardHeaderAction('Edit', appSchema, headerActions, criticalActions.length);
            setAnnotationPathFromEntitySetOrEntityType(
                editAction,
                generateParameters.entitySet,
                entityType,
                'UpdateHidden'
            );
        }
        if (isDeletable) {
            const deleteAction = addStandardHeaderAction(
                'Delete',
                appSchema,
                headerActions,
                criticalActions.length + 1
            );
            setAnnotationPathFromEntitySetOrEntityType(
                deleteAction,
                generateParameters.entitySet,
                entityType,
                'DeleteHidden'
            );
        }
    }

    const nextIndex = criticalActions.length + 2;

    //Check if isCopyAction is defined, this gets rendered first after delete
    addCopyAction(unCriticalActions, generateParameters.serviceAVT, appSchema, entityType, headerActions, nextIndex);

    addGroupedHeaderActions(
        unCriticalActions,
        generateParameters.serviceAVT,
        appSchema,
        entityType,
        headerActions,
        ActionType.Annotation,
        nextIndex
    );

    // Add custom header actions
    addHeaderFooterCustomActionsOP(appSchema, pageInManifest, DefinitionName.CustomHeaderActionOP, generateParameters);

    addRelatedAppsButton(headerActions);
    addEnumForActionAnchor(
        appSchema,
        DefinitionName.ObjectPageHeaderActions,
        DefinitionName.CustomHeaderActionPositionOP
    );
}

/**
 * Callback for adding a single action to footerActions of V4.
 *
 * @param {EntityType} entityType - current entity type
 * @param {Definition} appSchema - schema of the object page
 * @param {Definition} footerActions - list of footer actions, to be enhanced
 * @param {any} identification - UI.Identification annotation as present in AVT ConvertedMetadata
 */
function addFooterActionCallBackV4(
    entityType,
    appSchema: Definition,
    footerActions: Definition,
    identification: any
): any {
    const criticalActions: ActionListType[] = [];
    const unCriticalActions: ActionListType[] = [];

    identification.forEach((item, index) => {
        if (item['Determining'] && item.$Type === UIAnnotationTypes.DataFieldForAction) {
            if (item['Criticality']) {
                criticalActions.push({ index, item });
            } else {
                unCriticalActions.push({ index, item });
            }
        }
    });
    for (let index = 0; index < criticalActions.length; index++) {
        const action = criticalActions[index];
        addSingleFooterAction(action, ActionType.Criticality, entityType, appSchema, footerActions, index);
    }

    for (let index = 0; index < unCriticalActions.length; index++) {
        const action = unCriticalActions[index];
        addSingleFooterAction(
            action,
            ActionType.Annotation,
            entityType,
            appSchema,
            footerActions,
            criticalActions.length + index
        );
    }
}

/**
 * Object Page specific enhancements to addCustomColumnDefinition.
 *
 * @param appSchema - app schema of the object page
 * @param v4Page Current page in manifest
 * @param lineItemId - identifier of the current table in schema
 * @param facetIdInManifest - identifier of the current object page section in manifest
 * @param {GenerateAppSchemaParameters} generateParameters - Parameters required for generating the application schema.
 */
function addCustomColumnsOP(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    lineItemId: string,
    facetIdInManifest: string,
    generateParameters: GenerateAppSchemaParameters
) {
    // Create type specific copy of 'TableCustomColumnOP'
    const useDefinitionAsKey =
        lineItemId !== DefinitionName.LineItems &&
        lineItemId !== 'LineItemsOfSPV::LineItem' &&
        lineItemId !== `@${UIAnnotationTerms.LineItem}`;
    const customColumDefinitionKey = useDefinitionAsKey
        ? `${DefinitionName.CustomColumnOP}<${lineItemId}>`
        : DefinitionName.CustomColumnOP;
    const customColumnDefinition = appSchema.definitions[customColumDefinitionKey] as Definition;
    customColumnDefinition[SchemaTag.isViewNode] = true;
    customColumnDefinition.description = 'Custom Column';

    // Update definitions for type specific custom columns
    const schemaId = `${DefinitionName.CustomColumnOP}<${lineItemId}>`;
    addCustomColumnDefinition(appSchema, v4Page, generateParameters.logger, schemaId, lineItemId, facetIdInManifest);
    const facetKey = useDefinitionAsKey ? lineItemId : DefinitionName.PositionOP;
    parseSchemaDefinition(DefinitionName.PositionOP, facetKey, appSchema, useDefinitionAsKey);
    const positionId = useDefinitionAsKey ? `${DefinitionName.PositionOP}<${lineItemId}>` : DefinitionName.PositionOP;
    (customColumnDefinition.properties.position as Definition).$ref = `${DEFINITION_LINK_PREFIX}${positionId}`;
    addFragmentEnumForAnchor(appSchema, lineItemId, v4Page, positionId, facetIdInManifest.replace(/::/g, '/'));

    //Table custom column: Add enum for properties
    const lineItems = appSchema.definitions[lineItemId];
    if (lineItems) {
        addEnumForProperties(lineItems, customColumnDefinition);
    }
}

/**
 * Object Page specific enhancements to addCustomColumnDefinition.
 *
 * @param appSchema - app schema of the object page
 * @param v4Page Current page in manifest
 * @param lineItemId - identifier of the current table in schema
 * @param facetIdInManifest - identifier of the current object page section in manifest
 * @param {GenerateAppSchemaParameters} generateParameters - Parameters required for generating the application schema.
 * @returns Schema ID for actions definition.
 */
function addTableCustomActionsOP(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    lineItemId: string,
    facetIdInManifest: string,
    generateParameters: GenerateAppSchemaParameters
): string {
    // Create type specific copy of 'TableCustomActionOP'
    const useDefinitionAsKey =
        lineItemId !== DefinitionName.LineItems &&
        lineItemId !== 'LineItemsOfSPV::LineItem' &&
        lineItemId !== `@${UIAnnotationTerms.LineItem}`;
    const customActionDefinitionKey = useDefinitionAsKey
        ? `${DefinitionName.CustomTableActionOP}<${lineItemId}>`
        : DefinitionName.CustomTableActionOP;
    const customActionDefinition = appSchema.definitions[customActionDefinitionKey] as Definition;
    customActionDefinition[SchemaTag.isViewNode] = true;
    customActionDefinition.description = CUSTOM_ACTION_DEFINITION_DESCRIPTION;
    // Update definitions for type specific custom columns
    const schemaId = `${DefinitionName.CustomTableActionOP}<${lineItemId}>`;
    const schemaIdForActions = `${DefinitionName.Actions}<${prepareRef(lineItemId)}>`;
    addCustomActionDefinition(
        appSchema,
        v4Page,
        generateParameters.logger,
        schemaId,
        schemaIdForActions,
        facetIdInManifest
    );
    const facetKey = useDefinitionAsKey ? lineItemId : DefinitionName.CustomActionPositionOP;
    parseSchemaDefinition(DefinitionName.CustomActionPositionOP, facetKey, appSchema, useDefinitionAsKey);
    // Anchor selection
    const positionId = useDefinitionAsKey
        ? `${DefinitionName.CustomActionPositionOP}<${lineItemId}>`
        : DefinitionName.CustomActionPositionOP;
    (customActionDefinition.properties.position as Definition).$ref = `${DEFINITION_LINK_PREFIX}${positionId}`;
    addEnumForActionAnchor(appSchema, schemaIdForActions, positionId);

    configureAdditionalPropertiesForCustomActionsAndMenus(
        appSchema,
        schemaIdForActions,
        positionId,
        lineItemId,
        DefinitionName.CustomTableActionOP,
        customActionDefinitionKey
    );
    return schemaIdForActions;
}

/**
 * Object Page specific custom action enhancements to header and footer.
 *
 * @param appSchema - app schema of the object page
 * @param v4Page  - current page in manifest
 * @param customActionDefinitionKey - custom action key used in definitions
 * @param generateParameters - list of API input parameters
 */
function addHeaderFooterCustomActionsOP(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    customActionDefinitionKey: DefinitionName.CustomHeaderActionOP | DefinitionName.CustomFooterActionOP,
    generateParameters: GenerateAppSchemaParameters
): void {
    const customActionDefinition = appSchema.definitions[customActionDefinitionKey] as Definition;
    customActionDefinition[SchemaTag.isViewNode] = true;
    customActionDefinition.description = CUSTOM_ACTION_DEFINITION_DESCRIPTION;
    const schemaIdForActions =
        customActionDefinitionKey === DefinitionName.CustomHeaderActionOP
            ? DefinitionName.ObjectPageHeaderActions
            : DefinitionName.FooterActions;
    addHeaderFooterCustomActionDefinition(
        appSchema,
        v4Page,
        generateParameters.logger,
        customActionDefinitionKey,
        schemaIdForActions
    );
    const facetKey =
        customActionDefinitionKey === DefinitionName.CustomHeaderActionOP
            ? DefinitionName.CustomHeaderActionPositionOP
            : DefinitionName.CustomFooterActionPositionOP;
    parseSchemaDefinition(facetKey, facetKey, appSchema, false);
    // Anchor selection
    (customActionDefinition.properties.position as Definition).$ref = `${DEFINITION_LINK_PREFIX}${facetKey}`;

    if (appSchema.definitions[schemaIdForActions] && typeof appSchema.definitions[schemaIdForActions] === 'object') {
        appSchema.definitions[schemaIdForActions].additionalProperties =
            buildAdditionalPropertiesSchemaForCustomActionsAndMenus(customActionDefinitionKey);
    }
}

/**
 * Adds a table definition to the application schema based on the provided configuration.
 * This method defines the properties and annotations required for the table in the schema.
 *
 * @param params - The parameters for adding the table definition.
 * @param params.tableType - The type of the table to be added. For example, AnalyticalTable or ResponsiveTable.
 * @param params.facetKey - The key identifying the facet for which the table is being defined.
 * @param params.facet - The configuration object defining the properties of the facet.
 * @param params.appSchema - The overall application schema where the table definition should be added.
 * @param params.lineItemId - Optional ID for the line item, defaults to a generated schema facet key.
 * @param params.lineItemFacet - The current line item facet configuration.
 * @param params.v4Page - The current page configuration in the manifest.
 * @param params.isCollection - Indicates if the table is in a collection section. Defaults to false.
 */
function addTableDefinition({
    tableType,
    facetKey,
    facet,
    appSchema,
    lineItemId,
    lineItemFacet,
    v4Page,
    isCollection = false
}: AddTableDefinitionProps): void {
    const index = facetKey.lastIndexOf('::');
    const annotation = index > -1 ? facetKey.substring(index + 2) : facetKey;
    const tableDefinition = parseSchemaDefinition(tableType, facetKey, appSchema);
    if (facet.entityType && annotation) {
        tableDefinition[SchemaTag.annotationPath] = `/${facet.entityType.fullyQualifiedName}/${annotation}`;
    }
    tableDefinition.properties.columns = {
        $ref: `${DEFINITION_LINK_PREFIX}${lineItemId}`
    };
    (tableDefinition.properties.toolBar as Definition).$ref = getFacetDefinitionLink(
        DefinitionName.ObjectPageToolBar,
        facetKey
    );
    (tableDefinition.properties.quickVariantSelection as Definition).$ref = getFacetDefinitionLink(
        DefinitionName.QuickVariantSelectionV4OP,
        facetKey
    );
    tableDefinition[SchemaTag.target] = lineItemFacet.namespace;
}

/**
 * Object Page specific enhancements to handleFormIdentification.
 *
 * @param appSchema - app schema of the object page
 * @param v4Page - current page in manifest
 * @param formItemId - identifier of the current form in schema
 * @param facetIdInManifest - identifier of the current object page section in manifest
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @returns Schema ID for actions definition.
 */
function addFormCustomActionsOP(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    formItemId: string,
    facetIdInManifest: string,
    generateParameters: GenerateAppSchemaParameters
): string {
    const customActionDefinitionKey = `${DefinitionName.CustomFormActionOP}<${formItemId}>`;
    const customActionDefinition = appSchema.definitions[customActionDefinitionKey] as Definition;
    customActionDefinition[SchemaTag.isViewNode] = true;
    customActionDefinition.description = CUSTOM_ACTION_DEFINITION_DESCRIPTION;
    const schemaId = `${DefinitionName.CustomFormActionOP}<${formItemId}>`;
    const schemaIdForActions = `${DefinitionName.SectionActions}<${prepareRef(formItemId)}>`;
    addCustomActionDefinition(
        appSchema,
        v4Page,
        generateParameters.logger,
        schemaId,
        schemaIdForActions,
        facetIdInManifest,
        formItemId
    );
    parseSchemaDefinition(DefinitionName.CustomActionPositionOP, formItemId, appSchema, true);
    // Anchor selection
    const positionId = `${DefinitionName.CustomActionPositionOP}<${formItemId}>`;
    (customActionDefinition.properties.position as Definition).$ref = `${DEFINITION_LINK_PREFIX}${positionId}`;
    addEnumForActionAnchor(appSchema, schemaIdForActions, positionId);

    configureAdditionalPropertiesForCustomActionsAndMenus(
        appSchema,
        schemaIdForActions,
        positionId,
        formItemId,
        DefinitionName.CustomFormActionOP,
        customActionDefinitionKey
    );
    return schemaIdForActions;
}

/**
 * Extracts the navigation path from a line item ID or facet key.
 *
 * @param lineItemId - The line item ID that may contain a navigation prefix.
 * @returns The navigation path if present, undefined otherwise.
 * @example
 * ```typescript
 * extractNavigationPath("_Booking::@com.sap.vocabularies.UI.v1.LineItem")
 * // Returns: "_Booking"
 *
 * extractNavigationPath("@com.sap.vocabularies.UI.v1.LineItem")
 * // Returns: undefined (no navigation)
 * ```
 */
function extractNavigationPath(lineItemId?: string): string | undefined {
    if (!lineItemId || !lineItemId.includes('::')) {
        return undefined;
    }

    const navigationPath = lineItemId.split('::')[0];

    // Check if the first part looks like a navigation path (not an annotation)
    if (navigationPath && !navigationPath.startsWith('@')) {
        return navigationPath;
    }

    return undefined;
}

/**
 * Adds definitions for line items in object page sections to the app schema.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - Parameters required for generating the application schema.
 * @param {FacetConfig} facet - The facet configuration object defining the current facet.
 * @param {string} facetKey - The unique key associated with the facet.
 * @param {Definition} appSchema - The application schema to be updated with line item definitions.
 * @param {FacetConfig} lineItemFacet - The configuration of the line item facet being processed.
 * @param {ConvertedMetadata} oDataServiceAVT - Metadata converted from OData service annotations (AVT).
 * @param {v4.SapUiAppPageV4} v4Page - The V4 application page object.
 * @param {ExtensionLogger} logger - Logger instance for capturing warnings and diagnostic messages.
 * @param {boolean} [isCollection] - Indicates if the facet represents a collection of entities.
 */
function handleLineItem(
    generateParameters: GenerateAppSchemaParameters,
    facet: FacetConfig,
    facetKey: string,
    appSchema: Definition,
    lineItemFacet: FacetConfig,
    oDataServiceAVT: ConvertedMetadata,
    v4Page: v4.SapUiAppPageV4,
    logger: ExtensionLogger,
    isCollection?: boolean
): void {
    const lineItemId = getFacetKeyInSchema(facetKey);
    addTableDefinition({
        tableType: DefinitionName.ObjectPageTable,
        facetKey,
        facet,
        appSchema,
        lineItemId,
        lineItemFacet,
        v4Page,
        isCollection,
        generateParameters
    });

    const tableQVS = parseSchemaDefinition(DefinitionName.QuickVariantSelectionV4OP, facetKey, appSchema);
    tableQVS.properties.paths['items'].$ref = getFacetDefinitionLink(DefinitionName.AnnotationPathAsObject, facetKey);
    parseSchemaDefinition(DefinitionName.AnnotationPathAsObject, facetKey, appSchema);

    addEnumForSingleTabVariant(facet.entityType, appSchema, `${DefinitionName.AnnotationPathAsObject}<${lineItemId}>`);
    const dataForLineItem: LineItemDataForSchema = {
        isObjectPage: true,
        appSchema,
        lineItemAnnotation: lineItemFacet['target'] as AnnotationTerm<LineItem>,
        entityType: lineItemFacet['entityType'],
        oDataServiceAVT,
        lineItemId
    };
    addLineItemsTypeToSchema(
        dataForLineItem,
        DefinitionName.ObjectPageTableColumn,
        DefinitionName.CustomColumnOP,
        DefinitionName.CustomTableActionOP
    );
    // Add line item enums for enableMassEdit and manifestPath to table properties
    const tableType = DefinitionName.ObjectPageTable;
    // add columns to a enum, in this case enableMassEdit -> visibleFields and ignoredFields
    addEnumForEnableMassEdit(appSchema, `${tableType}<${lineItemId}>`, lineItemId, lineItemFacet.entityType);
    // add "manifestPath" to table properties
    const tableDefinitionKey = getFacetDefinitionKey(tableType, facetKey);
    const tableDefinition = appSchema.definitions?.[tableDefinitionKey];
    if (typeof tableDefinition === 'object') {
        addManifestPathsToPropertiesOP(
            appSchema,
            tableType,
            tableDefinition,
            {
                pageName: v4Page.id,
                manifest: generateParameters.manifest,
                sectionId: facetKey
            },
            true
        );
    }

    // Populate the set of fields and field groups for the creationFields property definition
    addOneOfForCreationFields(appSchema, lineItemFacet.entityType, DefinitionName.TableCreationModeOP, lineItemId);

    // Get table settings from manifest for decorator context
    const tableSettings = (
        v4Page?.options?.settings?.controlConfiguration?.[facetKey.replace('::', '/')] as { [key: string]: any }
    )?.tableSettings;

    // Check if navigation is defined to determine default creation mode
    const navigation = v4Page?.options?.settings?.navigation ?? {};
    const hasNavigation = Object.keys(navigation).length > 0;

    // Extract navigation path and check insertability
    const navigationPath = extractNavigationPath(lineItemId);
    const isInsertable = getInsertableForEntitySet(generateParameters.entitySet, navigationPath);

    const decoratorContext = {
        type: tableSettings?.type ?? v4.TableTypeV4.ResponsiveTable,
        sectionLayout: v4Page?.options?.settings?.sectionLayout,
        rowCountMode: tableSettings?.rowCountMode,
        rowCount: tableSettings?.rowCount,
        popinLayout: tableSettings?.popinLayout,
        analyticalConfiguration: tableSettings?.analyticalConfiguration,
        creationMode: tableSettings?.creationMode,
        hasNavigation,
        isInsertable,
        isCollection
    };

    // Apply decorators to Creation Mode definition properties (name, createAtEnd, creationFields, etc.)
    // AppSchema wrapper is needed so the @enums decorator can resolve $ref-based enum definitions
    // (e.g., TableCreationModeType) when filtering allowed values for specific table types
    const creationModeDefinitionKey = getFacetDefinitionKey(DefinitionName.TableCreationModeOP, lineItemId);
    const creationModeDefinition = appSchema.definitions[creationModeDefinitionKey] as Definition;

    if (creationModeDefinition) {
        const context: DecoratorContext = { schema: new AppSchema(appSchema) };
        new Decorator(context).init(TableCreationModeOP, decoratorContext, creationModeDefinition);
    }

    // Apply decorators to table definition properties (creationMode, rowCountMode, rowCount, popinLayout, analyticalConfiguration)
    const opTableDefinitionKey = `${DefinitionName.ObjectPageTable}<${lineItemId}>`;
    const opTableDefinition = appSchema.definitions[opTableDefinitionKey] as Definition;

    if (opTableDefinition) {
        const context: DecoratorContext = { schema: new AppSchema(appSchema) };
        new Decorator(context).init(v4controls.ObjectPageTable, decoratorContext, opTableDefinition);
    }

    addCustomColumnsOP(appSchema, v4Page, lineItemId, facetKey, generateParameters);
    addManifestPathsToDefinitionPropertiesOP(appSchema, v4Page.id, lineItemId, generateParameters, facetKey);

    const schemaIdForActions = addTableCustomActionsOP(appSchema, v4Page, lineItemId, facetKey, generateParameters);
    addManifestPathsToDefinitionPropertiesOP(appSchema, v4Page.id, schemaIdForActions, generateParameters, facetKey);
}

/**
 * Handles the creation and modification of a section table within the application schema.
 *
 * @param facet - The facet configuration object containing details for the section.
 * @param facetKey - The unique key for identifying the facet in the schema.
 * @param appSchema - The overall application schema where the section table is defined.
 * @param lineItemFacet - The facet configuration specific to line item processing.
 * @param generateParameters - Parameters required for generating the application schema.
 * @param v4Page - The OData V4 page object containing details about the target application page.
 * @param [sections] - The sections definition object where the section table might be added as a reference.
 * @param isCollection - Indicates if the table is in a collection section.
 * @returns The updated definition of the section table included or modified in the application schema.
 */
function handleSectionTable(
    facet: FacetConfig,
    facetKey: string,
    appSchema: Definition,
    lineItemFacet: FacetConfig,
    generateParameters: GenerateAppSchemaParameters,
    v4Page,
    sections?: Definition,
    isCollection?: boolean
): Definition {
    if (sections) {
        addPropertyRef(sections, facetKey, DefinitionName.ObjectPageSectionTableV4);
    }
    const sectionTable = parseSchemaDefinition(DefinitionName.ObjectPageSectionTableV4, facetKey, appSchema);
    addSectionTitle(facet, sectionTable, getFacetKeyInSchema(facetKey));
    addSectionDescription(facet, sectionTable, getFacetKeyInSchema(facetKey));
    sectionTable[SchemaTag.annotationPath] = facet.annotationPath;
    sectionTable[SchemaTag.target] = facet.namespace;

    // Update table reference to point to the facet-specific table definition
    const tableProperty = sectionTable.properties.table as Definition;
    if (tableProperty.anyOf) {
        // Legacy: multiple table types with anyOf
        tableProperty.anyOf = tableProperty.anyOf.map((item) => {
            const definition = item['$ref'].split('/').pop();
            return {
                ['$ref']: getFacetDefinitionLink(definition as DefinitionName, facetKey)
            };
        });
    } else if (tableProperty.$ref) {
        // New: single consolidated table type
        const definition = tableProperty.$ref.split('/').pop();
        tableProperty.$ref = getFacetDefinitionLink(definition as DefinitionName, facetKey);
    }
    handleLineItem(
        generateParameters,
        facet,
        facetKey,
        appSchema,
        lineItemFacet,
        generateParameters.serviceAVT,
        v4Page,
        generateParameters.logger,
        isCollection
    );
    return sectionTable;
}

/**
 * Adds definitions for Identification and Form facet items in object page sections to the app schema.
 *
 * @param {FacetConfig} facet - current facet
 * @param {v4.SapUiAppPageV4} v4Page Current page in manifest
 * @param {string} facetKey - facet key
 * @param {object} sections - schema of current sections definition
 * @param {object} appSchema - app specific schema of the object page that gets enhanced
 * @param {SectionType} sectionPrefix - prefix to distinguish Section and HeaderSection
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 */
function handleFormIdentification(
    facet: FacetConfig,
    v4Page: v4.SapUiAppPageV4,
    facetKey: string,
    sections: Definition,
    appSchema: Definition,
    sectionPrefix: SectionType,
    generateParameters: GenerateAppSchemaParameters
) {
    let definitionType: DefinitionName;
    if (sectionPrefix === SectionType.Section) {
        definitionType = DefinitionName.ObjectPageSectionForm;
    } else if (sectionPrefix === SectionType.HeaderSection) {
        definitionType = DefinitionName.ObjectPageHeaderSectionForm;
    }
    sections['properties'][facetKey] = {
        $ref: getFacetDefinitionLink(definitionType, facetKey)
    };
    const formSection: Definition = parseSchemaDefinition(definitionType, facetKey, appSchema);
    if (!facet.ID) {
        formSection.properties = {};
    }
    addSectionTitle(facet, formSection, facetKey);
    addSectionDescription(facet, formSection, facetKey);
    formSection[SchemaTag.annotationPath] = facet.annotationPath;
    formSection[SchemaTag.target] = facet.namespace;
    formSection[SchemaTag.isViewNode] = true;
    const targetId = handleForm(sectionPrefix, facet, formSection, appSchema, FioriElementsVersion.v4, facetKey);
    const formItemId = getFacetKeyInSchema(facetKey);
    if (facet.base === FacetBase.Form) {
        parseSchemaDefinition(DefinitionName.CustomFormActionOP, targetId, appSchema);
        const schemaIdForActions = addFormCustomActionsOP(appSchema, v4Page, targetId, formItemId, generateParameters);
        addManifestPathsToDefinitionPropertiesOP(
            appSchema,
            v4Page.id,
            schemaIdForActions,
            generateParameters,
            facetKey
        );
    }
    // Add custom fields
    parseSchemaDefinition(DefinitionName.CustomField, targetId, appSchema);
    const schemaIdForFields = addFormCustomFieldsOP(
        appSchema,
        v4Page,
        sectionPrefix,
        targetId,
        formItemId,
        generateParameters
    );
    addManifestPathsToDefinitionPropertiesOP(appSchema, v4Page.id, schemaIdForFields, generateParameters, facetKey);
}

/**
 * Adds definitions for header sections (DataPoint, Chart) in object page sections to the app schema.
 *
 * @param {FacetConfig} facet - current facet
 * @param {string} facetKey - facet key
 * @param {object} sections - schema of current sections definition
 * @param {object} appSchema - app specific schema of the object page that gets enhanced
 * @param {SectionType} facetDefinitionKey - key to identify new definition
 */
function handleHeaderFacet(
    facet: FacetConfig,
    facetKey: string,
    sections: Definition,
    appSchema: Definition,
    facetDefinitionKey: string
) {
    sections['properties'][facetKey] = {
        $ref: `${DEFINITION_LINK_PREFIX}${facetDefinitionKey}`
    };
    const sectionFacet = parseSchemaDefinition(
        DefinitionName.CommonHeaderFacetSettings,
        facetDefinitionKey,
        appSchema,
        false
    );
    addTargetTitleAndSectionDescription(facet, sectionFacet, facetKey);
    sectionFacet[SchemaTag.annotationPath] = facet.annotationPath;
    sectionFacet[SchemaTag.target] = facet.namespace;
    sectionFacet[SchemaTag.dataType] = determineDataTypeOfTarget(facet?.target);
    sectionFacet[SchemaTag.isViewNode] = true;
}

/**
 * Adds definitions for Chart items in object page sections to the app schema.
 *
 * @param {FacetConfig | Chart | object} facet - current facet.
 * @param {string} facetKey - facet key.
 * @param {object} appSchema - app specific schema of the object page that gets enhanced.
 * @param {EntityType} entityType - current entity type
 * @returns {Definition} - the new chart definition
 */
function handleChartActions(
    facet: FacetConfig | Chart | object,
    facetKey: string,
    appSchema: Definition,
    entityType: EntityType
): Definition {
    const actions = parseSchemaDefinition(DefinitionName.ObjectPageToolBarActions, facetKey, appSchema);
    actions.additionalProperties = false;
    actions.properties = {};
    if ((facet as Chart).Actions) {
        let i = 0;
        (facet as Chart).Actions.forEach((chartAction) => {
            const actionRecord = chartAction as DataFieldForAction;
            const description = getDataFieldDescription(actionRecord, entityType);
            const prefix = actionRecord.$Type.split(UIVOCABULARYDOT)[1];
            const actionId = (actionRecord.Action as string).replace('/', '::');
            const toolBarActionsProperty = (actions.properties[`${prefix}::${actionRecord.Action}`] = {
                $ref: getFacetDefinitionLink(DefinitionName.ObjectPageToolBarAction, actionId),
                description
            });
            toolBarActionsProperty[SchemaTag.propertyIndex] = i;
            toolBarActionsProperty[SchemaTag.annotationType] = actionRecord.$Type;
            const toolBarAction = parseSchemaDefinition(
                DefinitionName.ObjectPageToolBarAction,
                prepareRef(actionId),
                appSchema
            );
            toolBarAction[SchemaTag.annotationPath] = getFacetAnnotationPath(actionRecord);
            toolBarAction[SchemaTag.dataType] = determineDataType(actionRecord);
            toolBarAction.properties = {};
            i++;
        });
    }
    return actions;
}

/**
 * Adds definitions for Chart items in object page sections to the app schema.
 *
 * @param facet - current facet.
 * @param facetKey - facet key.
 * @param appSchema - app specific schema of the object page that gets enhanced.
 * @param entityType - current entity type
 * @returns - the new chart definition
 */
function handleChart(
    facet: FacetConfig | Chart | object,
    facetKey: string,
    appSchema: Definition,
    entityType?: EntityType
): { chart: Definition; chartActions: Definition } {
    const chart = parseSchemaDefinition(DefinitionName.ObjectPageChart, facetKey, appSchema);
    if (facet['Title']) {
        chart.description = facet['Title'];
    }
    chart[SchemaTag.annotationPath] = getFacetAnnotationPath(facet as PresentationVariantV4);
    chart[SchemaTag.dataType] = determineDataTypeOfTarget((facet as FacetConfig).target || facet);
    chart[SchemaTag.isViewNode] = true;
    chart.properties.toolBar['$ref'] = getFacetDefinitionLink(DefinitionName.ObjectPageToolBar, facetKey);
    const chartToolBar = parseSchemaDefinition(DefinitionName.ObjectPageToolBar, facetKey, appSchema);
    chartToolBar.properties.actions['$ref'] = getFacetDefinitionLink(DefinitionName.ObjectPageToolBarActions, facetKey);
    const chartActions = handleChartActions(facet, facetKey, appSchema, entityType);
    return { chart, chartActions };
}

/**
 * Adds definitions for Chart section in object page sections to the app schema.
 *
 * @param {FacetConfig} facet - current facet.
 * @param {string} facetKey - facet key.
 * @param {object} appSchema - app specific schema of the object page that gets enhanced.
 * @param {object} sections - schema of current sections definition.
 * @param {EntityType} entityType - current entity type
 * @param {boolean} isPVHeaderFacet - whether the facet is a Presentation Variant header facet
 * @param namespace - the namespace of the facet
 * @returns - the new chart definition
 */
function handleSectionChart(
    facet: FacetConfig | Chart,
    facetKey: string,
    appSchema: Definition,
    sections?: Definition,
    entityType: EntityType = (facet as FacetConfig).entityType,
    isPVHeaderFacet = false,
    namespace?: string
): { parentChartRef: Definition; chart?: Definition; chartActions?: Definition } {
    const definition = isPVHeaderFacet
        ? DefinitionName.CommonHeaderFacetSettings
        : DefinitionName.ObjectPageSectionChart;
    const facetDefinitionKey = isPVHeaderFacet
        ? DefinitionName.ObjectPageHeaderSectionChart
        : DefinitionName.ObjectPageSectionChart;
    if (sections) {
        addPropertyRef(sections, facetKey, definition);
    }
    const facetName = getFacetDefinitionKey(facetDefinitionKey, facetKey);
    const parentChartRef = parseSchemaDefinition(definition, facetName, appSchema, false);
    addTargetTitleAndSectionDescription(facet as FacetConfig, parentChartRef, facetKey);
    // For Presentation Variant header facets we don't add chart properties
    if (isPVHeaderFacet) {
        delete parentChartRef.properties.chart;
        return { parentChartRef };
    }
    parentChartRef[SchemaTag.annotationPath] = getFacetAnnotationPath(facet);
    parentChartRef.properties.chart['$ref'] = getFacetDefinitionLink(DefinitionName.ObjectPageChart, facetKey);
    const { chart, chartActions } = handleChart(
        (facet as FacetConfig).target || facet,
        facetKey,
        appSchema,
        entityType
    );
    chart[SchemaTag.target] = namespace;
    return { parentChartRef, chart, chartActions };
}

/**
 * Extracts the key and type of the visualization.
 *
 * @param hasTarget - whether the facet has a target
 * @param facetTarget - target of the facet
 * @param generateParameters - list of API input parameters
 * @returns - key and type of the visualization
 */
function getVisualizationKeyAndType(
    hasTarget: boolean,
    facetTarget: any,
    generateParameters: GenerateAppSchemaParameters
): { key: string; type: Visualization } {
    const alias = hasTarget ? facetTarget.fullyQualifiedName : facetTarget.targetString;
    const aliasPart = alias.split('@')[1].split('/')[0];
    const replacedAlias = replaceAlias(aliasPart, generateParameters.serviceAVT);
    const key = `@${replacedAlias}`;
    const type = key.split(UIVOCABULARYDOT)[1] as Visualization;
    return { key, type };
}

/**
 * Returns the target of the visualization.
 *
 * @param generateParameters - list of API input parameters
 * @param navigationPath - navigation path of the target
 * @param visualizationType - type of the visualization
 * @returns - target of the visualization
 */
function getTargetFromNavigation(
    generateParameters: GenerateAppSchemaParameters,
    navigationPath: string,
    visualizationType: string
): EntityTypeAnnotationsBase_UI {
    const navigationProp = generateParameters.entityType.navigationProperties.find(
        (prop) => prop.name === navigationPath
    );
    return navigationProp.targetType?.annotations?.UI[visualizationType];
}

/**
 * Returns the properties of the visualization.
 *
 * @param facet - the given facet from the UI annotations
 * @param facetKey - key of the facet, as used in annotation
 * @param generateParameters - list of API input parameters
 * @returns {VisualizationProps | undefined} - properties of the visualization
 */
function getVisualizationProps(
    facet: FacetConfig,
    facetKey: string,
    generateParameters: GenerateAppSchemaParameters
): VisualizationProps | undefined {
    const visualizations = facet.target[FacetBase.PresentationVariant]
        ? facet.target[FacetBase.PresentationVariant].Visualizations
        : facet.target['Visualizations'];
    if (!visualizations?.[0]) {
        return;
    }
    const $target = visualizations[0].$target;
    const facetTarget = $target || visualizations[0];

    const navigationPath = facetKey.lastIndexOf('::') > 0 ? facetKey.slice(0, facetKey.lastIndexOf('::')) : undefined;
    const { key, type } = getVisualizationKeyAndType(!!$target, facetTarget, generateParameters);
    const target = $target ? facetTarget : getTargetFromNavigation(generateParameters, navigationPath, type);
    const namespace = facet.target[SchemaTag.fullyQualifiedName].split('@')[0];

    return { key, type, target, namespace, navigationPath };
}

/**
 * Helper function to add property reference to the sections.
 *
 * @param {Definition} sections Definition to add property reference
 * @param {string} propertyName Property name to be added
 * @param {DefinitionName} definition - Key of the visualization
 * @param {string} link - Alternative to the property name to be added as link
 */
function addPropertyRef(sections: Definition, propertyName: string, definition: DefinitionName, link?: string) {
    sections.properties[propertyName] = {
        $ref: getFacetDefinitionLink(definition, link || propertyName)
    };
}

/**
 * Generates the property and navigation keys for a section based on the facet key and visualization properties.
 *
 * @param facetKey - The key of the facet.
 * @param props - The visualization properties containing key, type, target, namespace, and navigationPath.
 * @returns An object containing the navigationKey and propertyKey for the section.
 */
function getSectionPropertyRef(
    facetKey: string,
    props: VisualizationProps
): { navigationKey: string; propertyKey: string } {
    const propertyKey = `${facetKey}<${props.key}>`;
    const navigationKey = props.navigationPath ? `${props.navigationPath}::${props.key}` : props.key;
    return { propertyKey, navigationKey };
}

/**
 * Adds LineItem definition of object page section to the app schema.
 *
 * @param facet - the given facet from the UI annotations
 * @param facetKey - key of the facet, as used in annotation
 * @param appSchema - app schema of the object page
 * @param sections - schema of current sections definition.
 * @param props - visualization properties
 * @param generateParameters - list of API input parameters
 * @param v4Page Current page in manifest
 */
function addPVLineItemSection(
    facet: FacetConfig,
    facetKey: string,
    appSchema: Definition,
    sections: Definition,
    props: VisualizationProps,
    generateParameters: GenerateAppSchemaParameters,
    v4Page: v4.SapUiAppPageV4
) {
    const { propertyKey, navigationKey } = getSectionPropertyRef(facetKey, props);
    addPropertyRef(sections, propertyKey, DefinitionName.ObjectPageSectionTableV4, navigationKey);

    const sectionTable = handleSectionTable(
        facet,
        navigationKey,
        appSchema,
        {
            target: props.target,
            entityType: facet.entityType,
            annotationPath: `/${props.namespace}/${props.key}`,
            namespace: props.namespace
        } as FacetConfig,
        generateParameters,
        v4Page
    );

    sectionTable[SchemaTag.annotationPath] = facet.annotationPath;
    addTargetTitleAndSectionDescription(facet, sectionTable, facetKey);
    delete sectionTable[SchemaTag.isViewNode];
}

/**
 * Adds Chart definition of object page (header) section to the app schema.
 *
 * @param {FacetConfig} facet - the given facet from the UI annotations
 * @param {string} facetKey - key of the facet, as used in annotation
 * @param {Definition} appSchema - app schema of the object page
 * @param {object} sections - schema of current sections definition.
 * @param {VisualizationProps} props - visualization properties
 * @param {boolean} isPVHeaderFacet - flag to determine if the facet is a header facet
 */
function addPVChartSection(
    facet: FacetConfig,
    facetKey: string,
    appSchema: Definition,
    sections: Definition,
    props: VisualizationProps,
    isPVHeaderFacet = false
) {
    const definition = isPVHeaderFacet
        ? DefinitionName.ObjectPageHeaderSectionChart
        : DefinitionName.ObjectPageSectionChart;
    const { propertyKey, navigationKey } = getSectionPropertyRef(facetKey, props);
    addPropertyRef(sections, propertyKey, definition, navigationKey);

    const { parentChartRef } = handleSectionChart(
        props.target as Chart,
        navigationKey,
        appSchema,
        undefined,
        facet.entityType,
        isPVHeaderFacet,
        props.namespace
    );
    parentChartRef[SchemaTag.annotationPath] = facet.annotationPath;
    // If no header facet ID exists, remove properties as it is requirement for export to manifest.json
    if (!facet?.['ID']) {
        parentChartRef.properties = {};
    }
    if (isPVHeaderFacet) {
        // Fill description with Facet ID of (S)PV in case referenced Chart has no title. In case no facet ID exists, it is already prefilled with Chart and qualifier.
        addSectionTitle(facet, parentChartRef, facetKey);
        addChartFacetID(facet, parentChartRef);
    } else {
        addTargetTitleAndSectionDescription(facet, parentChartRef, facetKey);
    }
}

/**
 * Adds definitions for (selection) presentation variants in object page sections to the app schema.
 *
 * @param facet - the given facet from the UI annotations
 * @param facetKey - key of the facet, as used in annotation
 * @param appSchema - app schema of the object page
 * @param sections - schema of current sections definition.
 * @param sectionPrefix - prefix to distinguish Section and HeaderSection
 * @param generateParameters - list of API input parameters
 * @param v4Page Current page in manifest
 */
function handleSectionPresentationVariant(
    facet: FacetConfig,
    facetKey: string,
    appSchema: Definition,
    sections: Definition,
    sectionPrefix: SectionType,
    generateParameters: GenerateAppSchemaParameters,
    v4Page: v4.SapUiAppPageV4
) {
    if (!facet?.target) {
        log(generateParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOPRESENTATIONVARIANT', { key: facetKey })
        });
        return;
    }

    const visualizationProps = getVisualizationProps(facet, facetKey, generateParameters);
    const isPVHeaderFacet = sectionPrefix === SectionType.HeaderSection;

    if (!visualizationProps) {
        // Catch cases where no visualization properties are found
        return;
    }
    if (visualizationProps.type.startsWith(Visualization.Chart)) {
        addPVChartSection(facet, facetKey, appSchema, sections, visualizationProps, isPVHeaderFacet);
    } else if (visualizationProps.type.startsWith(Visualization.LineItem)) {
        addPVLineItemSection(facet, facetKey, appSchema, sections, visualizationProps, generateParameters, v4Page);
    }
}

/**
 * Method checks if passed facet contains any collection facet.
 *
 * @param {FacetConfig} facet - facet to check
 * @returns {boolean} Passed facet contains at least one collection facet
 */
function containsCollectionFacet(facet: FacetConfig): boolean {
    const childFacets = facet.facets || {};
    for (const id in childFacets) {
        const childFacet = childFacets[id];
        if (childFacet.base === FacetBase.CollectionFacet) {
            return true;
        }
    }
    return false;
}

/**
 * Object Page specific enhancements to addCustomColumnDefinition.
 *
 * @param {object} appSchema - app schema of the object page
 * @param {string} id - identifier of the current section facet
 * @param {string[]} featureToggles - supported features
 * @returns {string} Custom sub section reference id
 */
function createCustomSubSectionReference(appSchema: Definition, id: string, featureToggles?: string[]): string {
    const referenceId = `${DefinitionName.ObjectPageCustomSubSectionFragment}<${id}>`;
    const sourceDefinition = appSchema.definitions[DefinitionName.ObjectPageCustomSubSectionFragment];

    // Apply @validity decorators
    new Decorator({ schema: new AppSchema(appSchema) }).init(ObjectPageCustomSubSectionFragment);

    if (typeof sourceDefinition === 'object') {
        const relatedFacetSchema =
            typeof sourceDefinition.properties?.relatedFacet === 'object'
                ? sourceDefinition.properties.relatedFacet
                : {};
        const newRef: Definition = {
            ...sourceDefinition,
            properties: {
                ...sourceDefinition.properties,
                relatedFacet: {
                    ...relatedFacetSchema,
                    type: 'string'
                }
            }
        };
        // Building blocks
        if (isFeatureSupported(featureToggles, Features.BuildingBlocks)) {
            newRef['properties']['controls'] = {
                type: 'object'
            };
        }
        appSchema.definitions[referenceId] = newRef;
    }

    return referenceId;
}

/**
 * Method applies custom action definitions to custom sections.
 *
 * @param {Definition} appSchema - app schema of the object page
 * @param {v4.SapUiAppPageV4} v4Page Current page in manifest
 * @param {string} customSectionRef - reference to custom section definition
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 */
function applyCustomActionsToCustomSections(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    customSectionRef: string,
    generateParameters: GenerateAppSchemaParameters
): void {
    // add specific definitions for custom sections and custom actions from manifest
    const sectionsInManifest = v4Page.options?.settings?.content?.body?.sections;
    if (sectionsInManifest) {
        for (const sectionId in sectionsInManifest) {
            // in order to differentiate which custom actions belongs to which custom section, we need to create specific definition for each custom section
            const customSectionDef = parseSchemaDefinition(customSectionRef, sectionId, appSchema);
            // create definition for custom section actions
            const customSectionCustomActionsDef = parseSchemaDefinition(
                DefinitionName.ObjectPageCustomSectionActions,
                sectionId,
                appSchema
            );
            // create definition for custom section actions properties
            const customActionOPDef = parseSchemaDefinition(DefinitionName.CustomSectionActionOP, sectionId, appSchema);
            // assign reference for current custom sections to custom section actions definitions
            const customActionsDefinitionName = `${DefinitionName.ObjectPageCustomSectionActions}<${sectionId}>`;
            customSectionDef.properties['actions'] = {
                $ref: `${DEFINITION_LINK_PREFIX}${customActionsDefinitionName}`
            };
            const customActionDefinitionName = `${DefinitionName.CustomSectionActionOP}<${sectionId}>`;
            customSectionCustomActionsDef.additionalProperties = {
                $ref: `${DEFINITION_LINK_PREFIX}${customActionDefinitionName}`
            };
            parseSchemaDefinition(DefinitionName.CustomSectionActionPositionOP, sectionId, appSchema);
            const customActionPositionOPDefinitionName = `${DefinitionName.CustomSectionActionPositionOP}<${sectionId}>`;
            // assign reference for current custom section action position
            customActionOPDef.properties['position']['$ref'] =
                `${DEFINITION_LINK_PREFIX}${customActionPositionOPDefinitionName}`;
            // set custom action as view node
            customActionOPDef[SchemaTag.isViewNode] = true;
            // add custom actions definitions
            addCustomActionDefinition(
                appSchema,
                v4Page,
                generateParameters.logger,
                customActionDefinitionName,
                customActionsDefinitionName,
                sectionId
            );
            // add anchors
            addEnumForActionAnchor(appSchema, customActionsDefinitionName, customActionPositionOPDefinitionName);

            addManifestPathsToDefinitionPropertiesOP(
                appSchema,
                v4Page.id,
                customActionsDefinitionName,
                generateParameters,
                sectionId
            );
        }
    }
}

/**
 * Common method updates schema for custom sections or custom subsections.
 *
 * @param sections - sections definition
 * @param  appSchema - app schema of the object page
 * @param generateParameters - list of API input parameters
 * @param v4Page Current page in manifest
 * @param customSectionDefinitionName - The name of the custom section definition to be applied.
 * @param facet - the given facet from the UI annotations
 * @param facetKey - key of the facet, as used in annotation
 */
function applyCustomSections(
    sections: Definition,
    appSchema: Definition,
    generateParameters: GenerateAppSchemaParameters,
    v4Page: v4.SapUiAppPageV4,
    customSectionDefinitionName:
        | DefinitionName.ObjectPageCustomHeaderSectionFragment
        | DefinitionName.ObjectPageCustomSectionFragment,
    facet?: FacetConfig,
    facetKey?: string
): void {
    let isMergedSections = false;
    let customSectionRef: string;
    // facet and facetkey are used for custom subsection definition
    if (facet && facetKey) {
        isMergedSections = !containsCollectionFacet(facet);
        customSectionRef = createCustomSubSectionReference(appSchema, facetKey, generateParameters.featureToggles);
    } else {
        // else use custom header section definition
        customSectionRef = customSectionDefinitionName;
    }
    sections.additionalProperties = {
        $ref: `${DEFINITION_LINK_PREFIX}${customSectionRef}`
    };
    addCustomSectionDefinition(
        appSchema,
        sections,
        v4Page,
        customSectionRef,
        isMergedSections,
        facetKey,
        generateParameters.logger
    );
    const sectionDefinition = appSchema.definitions[customSectionRef] as Definition;
    if (typeof sectionDefinition === 'object') {
        addDescriptiveEnumDefinition(
            appSchema,
            sections,
            {
                schema: sectionDefinition,
                property: 'relatedFacet'
            },
            {
                resolveDefinition: true,
                keyProperties: [SchemaKeyName.id, SchemaKeyName.key],
                descriptionAsFallback: true
            }
        );
    }
    // add specific definitions for custom sections and custom actions
    if (customSectionRef.includes(DefinitionName.ObjectPageCustomSectionFragment)) {
        const customSectionCustomActions = appSchema.definitions[
            DefinitionName.ObjectPageCustomSectionActions
        ] as Definition;
        // initially define no actions for section
        customSectionCustomActions.properties = {};
        applyCustomActionsToCustomSections(appSchema, v4Page, customSectionRef, generateParameters);
    }
    const relatedFacet = sectionDefinition.properties.relatedFacet;
    if (
        customSectionRef?.includes(DefinitionName.ObjectPageCustomSubSectionFragment) &&
        isMergedSections &&
        typeof relatedFacet === 'object'
    ) {
        // Exclude annotation nodes
        const section = v4Page.options?.settings?.content?.body?.sections?.[facetKey];
        const subSections = section && 'subSections' in section ? section.subSections : {};
        for (let i = relatedFacet.oneOf.length - 1; i >= 0; i--) {
            const entry = relatedFacet.oneOf[i] as { const: string; hidden?: boolean };
            if (!subSections[entry.const]) {
                entry.hidden = true;
            }
        }
    }
}

/**
 * Adds definitions for collections in object page sections to the app schema.
 *
 * @param facet - the given facet from the UI annotations
 * @param facetKey - key of the facet, as used in annotation
 * @param appSchema - app schema of the object page
 * @param sections - schema of current sections definition.
 * @param sectionPrefix - prefix to distinguish Section and HeaderSection
 * @param generateParameters - list of API input parameters
 * @param v4Page Current page in manifest
 * @param level Collection level in tree
 */
function handleCollection(
    facet: FacetConfig,
    facetKey: string,
    appSchema: Definition,
    sections: Definition,
    sectionPrefix: SectionType,
    generateParameters: GenerateAppSchemaParameters,
    v4Page: v4.SapUiAppPageV4,
    level: number
) {
    const isCustomSubSectionsSupported = level === 0;
    const collectionFacet = (sections.properties[facetKey] = createDefaultSection(appSchema, facet, facetKey));
    const subsections: Definition = {
        // Custom subsections are allowed on first level of subsections
        additionalProperties: false,
        type: 'object',
        properties: {},
        description: 'Subsections'
    };
    collectionFacet.properties = {
        subsections
    };
    subsections[SchemaTag.annotationPath] = facet.annotationPath;
    subsections[SchemaTag.target] = facet.namespace;
    subsections[SchemaTag.keys] = [
        {
            name: SchemaKeyName.id,
            value: facet.ID
        }
    ];
    subsections[SchemaTag.isViewNode] = true;
    const nextLevel = level + 1;
    for (const key in facet.facets) {
        addSection(generateParameters, facet.facets, key, subsections, appSchema, sectionPrefix, v4Page, nextLevel);
    }
    if (isCustomSubSectionsSupported) {
        applyCustomSections(
            subsections,
            appSchema,
            generateParameters,
            v4Page,
            DefinitionName.ObjectPageCustomSectionFragment,
            facet,
            facetKey
        );
    }
}

/**
 * Creates a section definition in app schema.
 *
 * @param generateParameters - list of API input parameters
 * @param facets - list of all facets
 * @param facetKey - facet key
 * @param sections - schema of current sections definition
 * @param appSchema - app specific schema of the object page that gets enhanced.
 * @param sectionPrefix - prefix to distinguish Section and HeaderSection
 * @param v4Page Current page in manifest
 * @param [level] Section level in tree
 */
function addSection(
    generateParameters: GenerateAppSchemaParameters,
    facets: FacetConfigs,
    facetKey: string,
    sections: Definition,
    appSchema: Definition,
    sectionPrefix: SectionType,
    v4Page: v4.SapUiAppPageV4,
    level = 0
): void {
    const facet: FacetConfig = facets[facetKey];
    const facetDefinitionKey = `ObjectPage${sectionPrefix}${facet.base}<${getFacetKeyInSchema(facetKey)}>`;
    switch (facet.base) {
        case FacetBase.PresentationVariant:
        case FacetBase.SelectionPresentationVariant: {
            handleSectionPresentationVariant(
                facet,
                facetKey,
                appSchema,
                sections,
                sectionPrefix,
                generateParameters,
                v4Page
            );
            break;
        }
        case FacetBase.CollectionFacet: {
            handleCollection(facet, facetKey, appSchema, sections, sectionPrefix, generateParameters, v4Page, level);
            break;
        }
        case FacetBase.LineItem: {
            if (sectionPrefix !== SectionType.HeaderSection) {
                const lineItemFacet = facets[facetKey];
                handleSectionTable(
                    facet,
                    facetKey,
                    appSchema,
                    lineItemFacet,
                    generateParameters,
                    v4Page,
                    sections,
                    level > 0 ? true : false
                );
            }
            break;
        }
        case FacetBase.Form:
        case FacetBase.Identification: {
            handleFormIdentification(facet, v4Page, facetKey, sections, appSchema, sectionPrefix, generateParameters);
            break;
        }
        case FacetBase.DataPoint: {
            handleHeaderFacet(facet, facetKey, sections, appSchema, facetDefinitionKey);
            break;
        }
        case FacetBase.Chart: {
            if (sectionPrefix === SectionType.HeaderSection) {
                handleHeaderFacet(facet, facetKey, sections, appSchema, facetDefinitionKey);
            } else if (sectionPrefix === SectionType.Section) {
                handleSectionChart(facet, facetKey, appSchema, sections);
            }
            break;
        }
        case FacetBase.Address:
        case FacetBase.Contact: {
            handleAddressContact(facet, facetKey, sections, appSchema, sectionPrefix);
            break;
        }
        default: {
            sections['properties'][facetKey] = createDefaultSection(appSchema, facet, facetKey);
        }
    }
}

/**
 * Derives the sections from the UI.Facets annotation and adds them to the schema.
 *
 * @param generateParameters - list of API input parameters
 * @param appSchema - application specific JSON schema of the object page
 * @param v4Page - current page
 */
function addSections(
    generateParameters: GenerateAppSchemaParameters,
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4
): void {
    // Get facet annotations
    const facets: FacetConfigs = getObjectPageFacets(
        generateParameters.entityType,
        generateParameters.serviceAVT,
        FioriElementsVersion.v4,
        generateParameters.logger
    );
    if (!facets) {
        delete appSchema.definitions[DefinitionName.ObjectPageSubSections];
        return;
    }

    const sections = appSchema.definitions[DefinitionName.Sections] as Definition;

    for (const facetKey in facets) {
        addSection(generateParameters, facets, facetKey, sections, appSchema, SectionType.Section, v4Page);
    }
    // add custom sections
    applyCustomSections(
        sections,
        appSchema,
        generateParameters,
        v4Page,
        DefinitionName.ObjectPageCustomSectionFragment
    );
    addManifestPathsToDefinitionPropertiesOP(appSchema, v4Page.id, DefinitionName.Sections, generateParameters);
}

/**
 * Derives the header sections from the UI.HeaderFacets annotation and adds them to the schema.
 *
 * @param generateParameters - list of API input parameters
 * @param appSchema - application specific JSON schema of the object page
 * @param entityType - current entity type
 * @param v4Page - current page in manifest
 */
function addHeaderSections(
    generateParameters: GenerateAppSchemaParameters,
    appSchema: Definition,
    entityType: EntityType,
    v4Page: v4.SapUiAppPageV4
): void {
    // Get facet annotations
    const facets: FacetConfigs = getObjectPageHeaderFacets(
        entityType,
        generateParameters.serviceAVT,
        FioriElementsVersion.v4,
        generateParameters.logger
    );
    const sections = appSchema.definitions[DefinitionName.HeaderSections] as Definition;
    sections.properties = {};
    sections.additionalProperties = false;

    if (!facets) {
        return;
    }
    // add annotation header sections
    for (const facetKey in facets) {
        addSection(generateParameters, facets, facetKey, sections, appSchema, SectionType.HeaderSection, v4Page);
    }
    // add custom header sections
    applyCustomSections(
        sections,
        appSchema,
        generateParameters,
        v4Page,
        DefinitionName.ObjectPageCustomHeaderSectionFragment
    );
    //delete generic definitions
    delete appSchema.definitions[DefinitionName.ObjectPageHeaderSectionForm];
    delete appSchema.definitions[DefinitionName.ObjectPageHeaderSectionChart];
    delete appSchema.definitions[DefinitionName.ObjectPageHeaderSectionDataPoint];
    delete appSchema.definitions[DefinitionName.ObjectPageHeaderSectionContact];
    delete appSchema.definitions[DefinitionName.ObjectPageHeaderSectionAddress];
}

/**
 * Add Object Page Header to app-specific schema.
 *
 * @param generateParameters - list of API input parameters
 * @param appSchema Schema of the object page
 * @param v4Page - current page in manifest
 */
function addHeader(
    generateParameters: GenerateAppSchemaParameters,
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4
): void {
    const entityType = generateParameters.entityType;
    addCommonHeaderSchema(appSchema, entityType);

    // Add header actions
    addHeaderActions(appSchema, entityType, generateParameters, addHeaderActionCallBackV4, v4Page);
    addManifestPathsToDefinitionPropertiesOP(
        appSchema,
        v4Page.id,
        DefinitionName.ObjectPageHeaderActions,
        generateParameters
    );

    // Add header sections
    if (entityType) {
        appSchema.definitions[DefinitionName.HeaderSections][SchemaTag.annotationPath] =
            `/${entityType.fullyQualifiedName}/@${UIAnnotationTerms.HeaderFacets}`;
    }
    addHeaderSections(generateParameters, appSchema, entityType, v4Page);
    addManifestPathsToDefinitionPropertiesOP(appSchema, v4Page.id, DefinitionName.HeaderSections, generateParameters);
}

/**
 * Add Object Page Footer to app-specific schema.
 *
 * @param generateParameters - list of API input parameters
 * @param appSchema Schema of the object page
 * @param v4Page - current page in manifest
 */
function addFooter(
    generateParameters: GenerateAppSchemaParameters,
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4
): void {
    // Currently only actions are supported for footer
    addFooterActions(appSchema, generateParameters, FioriElementsVersion.v4, addFooterActionCallBackV4);
    addHeaderFooterCustomActionsOP(appSchema, v4Page, DefinitionName.CustomFooterActionOP, generateParameters);
    addEnumForActionAnchor(appSchema, DefinitionName.FooterActions, DefinitionName.CustomFooterActionPositionOP);
    addManifestPathsToDefinitionPropertiesOP(appSchema, v4Page.id, DefinitionName.FooterActions, generateParameters);
}

/**
 * Adds 'manifestPath' to each property in a definition based on metadata sync rules.
 *
 * @param appSchema - Full schema.
 * @param definitionName - The name of the definition/class whose properties are being processed.
 * @param definition - The definition object containing property schemas.
 * @param pathParams - Additional context required to compute manifest paths.
 * @param handleRelative - A flag indicating whether to use relative paths for definition properties.
 */
function addManifestPathsToPropertiesOP(
    appSchema: Definition,
    definitionName: string,
    definition: Definition,
    pathParams: ManifestPathParams,
    handleRelative = false
): void {
    addManifestPathsToProperties(
        appSchema,
        definitionName,
        definition,
        {
            ...pathParams,
            targetAnnotation: pathParams.sectionId?.replace('::', '/'),
            targetAnnotationEncoded: pathParams.sectionId,
            pathsParts: [
                pathParams.sectionId,
                pathParams.columnKey,
                pathParams.targetAnnotation,
                pathParams.configObject,
                pathParams.originalIds
            ]
        },
        handleRelative
    );
}

/**
 * Adds 'manifestPath' to static nodes in schema.
 *
 * @param appSchema - Full schema.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToStaticNodes(appSchema: Definition, pathParams: ManifestPathParams): void {
    // Add "manifestPath" for current page
    appSchema['manifestPath'] = getPageManifestPath(pathParams);
    // Root level - rules stored on page class level
    addManifestPathsToPropertiesOP(appSchema, PageTypeV4.ObjectPage, appSchema, pathParams);
    // Other definitions
    const definitions = [DefinitionName.ObjectPageHeader, DefinitionName.ObjectPageLayout];
    for (const definitionName of definitions) {
        const definition = appSchema.definitions?.[definitionName];
        if (typeof definition === 'object') {
            addManifestPathsToPropertiesOP(appSchema, definitionName, definition, pathParams);
        }
    }
}

/**
 * Generates an app specific schema out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {object} genericSchema  - generic JSON schema of an object page
 * @param {string} contextPath - contextPath of the given page
 * @param {object} genericMacrosSchema - generic JSON schema of custom page
 * @returns the app specific JSON schema
 */
export function generateObjectPageSchemaV4(
    generateParameters: GenerateAppSchemaParameters,
    genericSchema: object,
    contextPath: string,
    genericMacrosSchema?: object
): object {
    const appSchema: Definition = JSON.parse(JSON.stringify(genericSchema));

    // Change reference to generated sections
    appSchema.properties.sections = {
        $ref: `${DEFINITION_LINK_PREFIX}${DefinitionName.Sections}`
    };
    if (
        !generateParameters.manifest[ManifestSection.ui5].routing ||
        !generateParameters.manifest[ManifestSection.ui5].routing.targets
    ) {
        log(generateParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOROUTING'),
            location: {
                path: MANIFESTPATH
            }
        });
        return appSchema;
    }

    //initialize sections
    const sections = (appSchema.definitions[DefinitionName.Sections] = {
        type: 'object',
        properties: {},
        additionalProperties: false,
        description: DefinitionName.Sections
    } as Definition);
    sections[SchemaTag.isViewNode] = true;
    if (generateParameters.entityType) {
        const pages = generateParameters.manifest[ManifestSection.ui5].routing
            .targets as unknown as v4.SapUiAppPageV4[];
        if (!pages) {
            log(generateParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOPAGES', { appId: generateParameters.manifest['sap.app']['id'] }),
                location: {
                    path: MANIFESTPATH,
                    range: [ManifestSection.generic]
                }
            });
            return appSchema;
        }
        const v4Page = findPageV4(pages, v4.FE_TEMPLATE_V4_OBJECT_PAGE, generateParameters.entitySet, contextPath);
        if (!v4Page) {
            return appSchema;
        }

        // Add Header
        addHeader(generateParameters, appSchema, v4Page);

        // Add sections
        sections[SchemaTag.isViewNode] = true;
        if (generateParameters.entityType) {
            sections[SchemaTag.annotationPath] =
                `/${generateParameters.entityType.fullyQualifiedName}/@${UIAnnotationTerms.Facets}`;
        }

        // Custom extension fragments
        if (isFeatureSupported(generateParameters.featureToggles, Features.BuildingBlocks) && genericMacrosSchema) {
            appSchema['definitions'][DefinitionName.ObjectPageCustomSectionFragment]['properties']['controls'] = {
                type: 'object'
            };
            try {
                resolveFragments(appSchema, genericMacrosSchema, generateParameters, v4Page.id);
            } catch (error) {
                log(generateParameters.logger, {
                    severity: LogSeverity.Error,
                    message: error
                });
            }
        }
        addSections(generateParameters, appSchema, v4Page);

        // Add Footer (actions only)
        addFooter(generateParameters, appSchema, v4Page);

        // Add "manifestPath" on root.page level properties
        addManifestPathsToStaticNodes(appSchema, {
            pageName: v4Page.id,
            manifest: generateParameters.manifest
        });
    }

    // Delete generic definitions from schema
    delete appSchema.definitions[DefinitionName.CommonHeaderFacetSettings];
    delete appSchema.definitions[DefinitionName.GenericColumnsOP];
    delete appSchema.definitions[DefinitionName.GenericSections];
    delete appSchema.definitions[DefinitionName.GenericSubSections];
    delete appSchema.definitions[DefinitionName.ObjectPageChart];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionAddress];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionChart];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionContact];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionDataPoint];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionForm];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionTableV4];
    delete appSchema.definitions[DefinitionName.ObjectPageSectionPresentationVariant];
    delete appSchema.definitions[DefinitionName.ObjectPageSubSections];
    delete appSchema.definitions[DefinitionName.ObjectPageTable];
    delete appSchema.definitions[DefinitionName.ObjectPageToolBar];
    delete appSchema.definitions[DefinitionName.ObjectPageToolBarAction];
    delete appSchema.definitions[DefinitionName.ObjectPageToolBarActions];
    delete appSchema.definitions[DefinitionName.QuickVariantSelectionV4OP];
    updatePropertyIndices(appSchema, ROOT_PROPERTIES_ORDER);
    return appSchema;
}
//

/**
 * Enhances the Object Page schema with custom field definitions for a specific form section.
 *
 * This function extends the app schema (`appSchema`) by adding schema properties for custom fields
 * associated with a section on the Object Page.
 *
 * @param appSchema - The application schema object representing the Object Page structure.
 * @param v4Page - The current Object Page configuration from the manifest (V4 version).
 * @param sectionPrefix - Indicates whether the section is a header or a standard section (from `SectionType` enum).
 * @param formItemId - Unique identifier of the current form within the schema.
 * @param fieldGroupInManifest - Identifier of the corresponding section (facet) in the manifest.
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @returns Schema ID for fields definition.
 */
function addFormCustomFieldsOP(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    sectionPrefix: SectionType,
    formItemId: string,
    fieldGroupInManifest: string,
    generateParameters: GenerateAppSchemaParameters
): string {
    const customFieldDefinitionKey = `${DefinitionName.CustomField}<${formItemId}>`;
    const customFieldDefinition = appSchema.definitions[customFieldDefinitionKey] as Definition;
    customFieldDefinition[SchemaTag.isViewNode] = true;
    customFieldDefinition.description = CUSTOM_FIELD_DEFINITION_DESCRIPTION;
    const schemaId = `${DefinitionName.CustomField}<${formItemId}>`;
    const fieldsDefinitionKey =
        sectionPrefix === SectionType.HeaderSection ? DefinitionName.HeaderSectionFields : DefinitionName.SectionFields;
    const schemaIdForFields = `${fieldsDefinitionKey}<${prepareRef(formItemId)}>`;
    addCustomFieldDefinition(
        appSchema,
        v4Page,
        fieldGroupInManifest,
        generateParameters.logger,
        schemaId,
        schemaIdForFields
    );
    parseSchemaDefinition(DefinitionName.CustomFieldPosition, formItemId, appSchema, true);
    // Anchor selection
    const positionId = `${DefinitionName.CustomFieldPosition}<${formItemId}>`;
    (customFieldDefinition.properties.position as Definition).$ref = `${DEFINITION_LINK_PREFIX}${positionId}`;
    // Add enum for custom field anchor property
    addDescriptiveEnumDefinition(
        appSchema,
        appSchema.definitions[schemaIdForFields],
        {
            definition: positionId,
            property: 'anchor'
        },
        {
            resolveDefinition: true
        }
    );
    if (appSchema.definitions[schemaIdForFields]) {
        (appSchema.definitions[schemaIdForFields] as Definition).additionalProperties = {
            $ref: `${DEFINITION_LINK_PREFIX}${customFieldDefinitionKey}`
        };
    }
    return schemaIdForFields;
}

/**
 * Enhances the Object Page schema with manifest paths for definition and properties.
 *
 * This function extends the app schema (`appSchema`) by adding manifestPath to definition and properties on the Object Page.
 *
 * @param {Definition} appSchema - The application schema object representing the Object Page structure.
 * @param {string} pageId - Identifier of the page.
 * @param {string} definitionName - Identifier of the corresponding definition.
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters.
 * @param {string} facetKey - Identifier of the section (facet key or custom section id) in manifest in case of custom columns.
 */
function addManifestPathsToDefinitionPropertiesOP(
    appSchema: Definition,
    pageId: string,
    definitionName: string,
    generateParameters: GenerateAppSchemaParameters,
    facetKey?: string
): void {
    const matchingDefinition = appSchema.definitions[definitionName] as Definition;
    const properties = matchingDefinition?.properties ?? {};
    const isSections = [`${DefinitionName.HeaderSections}`, `${DefinitionName.Sections}`].includes(definitionName);

    for (const key in properties) {
        const pathParams: ManifestPathParams = {
            pageName: pageId,
            manifest: generateParameters.manifest,
            sectionId: undefined,
            columnKey: undefined,
            originalIds: undefined
        };
        const property = matchingDefinition.properties[key] as Definition;
        // Manifest paths for actions inside actions menus have to be resolved from scratch
        // In schema those are considered as view nodes and won't be resolved by "addManifestPathsToPropertiesOP"
        if (property.$ref && property.$ref.endsWith(`ActionMenu<${key}>`)) {
            const menuDefinitionKey = property.$ref.replace(DEFINITION_LINK_PREFIX, '');
            const menuDefinition = appSchema.definitions[menuDefinitionKey] as Definition;
            const menuActions = menuDefinition.properties['actions'] as Definition;
            const menuActionsDefinitionKey = menuActions.$ref.replace(DEFINITION_LINK_PREFIX, '');
            addManifestPathsToDefinitionPropertiesOP(
                appSchema,
                pageId,
                menuActionsDefinitionKey,
                generateParameters,
                facetKey
            );
        }
        if (isSections) {
            // Current logic checks for subsections at first level, we meed to introduce recursion? for next levels
            const subSections = property.properties?.['subsections']?.['properties'];
            if (subSections) {
                Object.keys(subSections).forEach((subSectionId: string) => {
                    // subsections requires array like paths for sync rule resolution
                    const subSection = subSections[subSectionId];
                    pathParams.originalIds = ['sections', key, 'subsections', subSectionId];
                    pathParams.sectionId = key;
                    pathParams.columnKey = subSectionId;
                    addManifestPathsToPropertiesOP(appSchema, undefined, subSection, pathParams, true);
                });
            } else {
                // Check OP facets for sectionId resolution
                const facets = getObjectPageHeaderFacets(
                    generateParameters.entityType,
                    generateParameters.serviceAVT,
                    FioriElementsVersion.v4,
                    generateParameters.logger
                );
                pathParams.sectionId = facets[key]?.ID ?? key;
            }
        } else {
            pathParams.sectionId = facetKey;
            pathParams.columnKey = key;
        }
        addManifestPathsToPropertiesOP(appSchema, undefined, property, pathParams, true);
    }
}
